import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { ADMIN_CATEGORIES } from '../../constants/categories';
import { createVehicle, updateVehicle } from '../../services/vehicleService';
import { uploadVehicleImage, deleteVehicleImage } from '../../services/storageService';

const emptyForm = {
  name: '',
  category: 'sedan',
  tagline: '',
  features: [''],
  status: 'available',
  sortOrder: 0,
};

const VehicleForm = ({ vehicle, onClose, onSaved }) => {
  const isEditing = Boolean(vehicle);
  const [form, setForm] = useState(
    vehicle
      ? {
          name: vehicle.name || '',
          category: vehicle.category || 'sedan',
          tagline: vehicle.tagline || '',
          features: vehicle.features?.length ? vehicle.features : [''],
          status: vehicle.status || 'available',
          sortOrder: vehicle.sortOrder ?? 0,
        }
      : emptyForm
  );
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(vehicle?.imageUrl || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFeatureChange = (index, value) => {
    const features = [...form.features];
    features[index] = value;
    setForm((prev) => ({ ...prev, features }));
  };

  const addFeature = () => {
    setForm((prev) => ({ ...prev, features: [...prev.features, ''] }));
  };

  const removeFeature = (index) => {
    setForm((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const features = form.features.map((f) => f.trim()).filter(Boolean);
    if (!form.name.trim() || !form.tagline.trim() || features.length === 0) {
      setError('Name, tagline, and at least one feature are required.');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        tagline: form.tagline.trim(),
        features,
        status: form.status,
        sortOrder: Number(form.sortOrder) || 0,
      };

      if (isEditing) {
        let imageUrl = vehicle.imageUrl || '';

        if (imageFile) {
          if (vehicle.imageUrl) {
            await deleteVehicleImage(vehicle.imageUrl);
          }
          imageUrl = await uploadVehicleImage(imageFile, vehicle.id);
        }

        await updateVehicle(vehicle.id, { ...payload, imageUrl });
      } else {
        const id = await createVehicle({ ...payload, imageUrl: '' });

        if (imageFile) {
          const imageUrl = await uploadVehicleImage(imageFile, id);
          await updateVehicle(id, { imageUrl });
        }
      }

      onSaved();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save vehicle.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="glass-card w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="font-display text-2xl font-bold text-white">
            {isEditing ? 'Edit Vehicle' : 'Add Vehicle'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#3DED97]/50 focus:outline-none"
                placeholder="Mercedes-Benz G63 AMG"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Category</label>
              <select
                value={form.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#3DED97]/50 focus:outline-none"
              >
                {ADMIN_CATEGORIES.map((cat) => (
                  <option key={cat.id} value={cat.id} className="bg-gray-900">
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Tagline</label>
            <input
              type="text"
              value={form.tagline}
              onChange={(e) => handleChange('tagline', e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#3DED97]/50 focus:outline-none"
              placeholder="Short description of the vehicle"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Status</label>
              <select
                value={form.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#3DED97]/50 focus:outline-none"
              >
                <option value="available" className="bg-gray-900">Available</option>
                <option value="reserved" className="bg-gray-900">Reserved</option>
                <option value="sold" className="bg-gray-900">Sold</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Sort Order</label>
              <input
                type="number"
                value={form.sortOrder}
                onChange={(e) => handleChange('sortOrder', e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#3DED97]/50 focus:outline-none"
                min="0"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Features</label>
            <div className="space-y-2">
              {form.features.map((feature, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => handleFeatureChange(index, e.target.value)}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#3DED97]/50 focus:outline-none"
                    placeholder="Feature name"
                  />
                  {form.features.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="p-2.5 rounded-xl text-red-400 hover:bg-red-500/10 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addFeature}
                className="flex items-center gap-2 text-sm text-[#3DED97] hover:text-[#B0FC38] transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add feature
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Vehicle Image</label>
            <p className="text-xs text-gray-500 mb-2">Images are stored in Firebase Storage</p>
            {imagePreview && (
              <img
                src={imagePreview}
                alt="Preview"
                className="w-full h-48 object-cover rounded-xl mb-3 border border-white/10"
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#3DED97] file:text-black hover:file:bg-[#B0FC38]"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-6 py-3 rounded-xl font-semibold text-gray-300 glass-card hover:border-white/20 transition-all">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 btn-primary !py-3 !rounded-xl disabled:opacity-50"
            >
              {saving ? 'Saving...' : isEditing ? 'Update Vehicle' : 'Add Vehicle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VehicleForm;
